import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ParsedQs } from 'qs';

import history from 'utils/history';
import { isObjectEmpty } from 'utils/object';
import { handleError } from 'utils/errorHandler';
import { parse, stringify } from 'utils/queryParam';

import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT_ORDER,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_NOTE_STATUS_FILTER,
} from 'constants/appConstants';
import { ORDER_BY } from 'constants/myTeam';

import * as noteService from 'services/note';

import type {
  NoteSummaryFilters,
  NoteSummaryQuery,
  NoteSummaryMeta,
  NoteSummaryItem,
  UseNoteSummaryResult,
} from './interfaces';

const getQueryStringValue = (value: string | string[] | ParsedQs | ParsedQs[] | undefined): string | undefined => {
  return typeof value === 'string' ? value : undefined;
};

const getQueryNumberValue = (value: string | string[] | ParsedQs | ParsedQs[] | undefined): number | undefined => {
  const rawValue = getQueryStringValue(value);
  if (!rawValue) {
    return undefined;
  }

  const parsedValue = Number(rawValue);

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : undefined;
};

interface UseNoteSummaryProps {
  pathname: string;
  search: string;
}

export function useNoteSummary({ pathname, search }: UseNoteSummaryProps): UseNoteSummaryResult {
  const queryParams = useMemo(
    () =>
      parse(search, {
        decoder: (str: string) => decodeURIComponent(str),
      }) as NoteSummaryQuery,
    [search]
  );

  const [filters, setFilters] = useState<NoteSummaryFilters>({
    status: getQueryStringValue(queryParams.status) ?? DEFAULT_NOTE_STATUS_FILTER.value,
    role: getQueryStringValue(queryParams.role),
    page: getQueryNumberValue(queryParams.page) ?? DEFAULT_PAGE_NUMBER,
    size: getQueryNumberValue(queryParams.pageSize) ?? getQueryNumberValue(queryParams.size) ?? DEFAULT_PAGE_SIZE,
    order: getQueryStringValue(queryParams.order) ?? DEFAULT_SORT_ORDER,
    sortBy: getQueryStringValue(queryParams.sortBy),
  });
  const [pageData, setPageData] = useState<NoteSummaryMeta>({});
  const [pageCount, setPageCount] = useState(DEFAULT_PAGE_NUMBER);
  const [columnToSortBy, setColumnToSortBy] = useState('actionDate');
  const [isAscendingOrdered, setIsAscendingOrdered] = useState(true);
  const [notes, setNotes] = useState<NoteSummaryItem[]>([]);
  const [isFetchingNotes, setIsFetchingNotes] = useState(false);

  const updateUrl = useCallback(
    (value: NoteSummaryFilters) => {
      const queryParam = stringify('', value, { arrayFormat: 'comma', encode: false });
      history.push({
        pathname,
        search: queryParam,
      });
    },
    [pathname]
  );

  const getParams = useCallback(
    (prev: NoteSummaryFilters): NoteSummaryFilters => ({
      ...prev,
      status: getQueryStringValue(queryParams.status) ?? prev.status,
      role: getQueryStringValue(queryParams.role) ?? prev.role,
      page: getQueryNumberValue(queryParams.page) ?? prev.page,
      size: getQueryNumberValue(queryParams.size) ?? prev.size,
      order: getQueryStringValue(queryParams.order) ?? prev.order,
      sortBy: getQueryStringValue(queryParams.sortBy) ?? prev.sortBy,
    }),
    [queryParams]
  );

  const onPageSizeChange = useCallback(
    (selectedSize: number) => {
      setFilters((prev) => {
        const params = getParams(prev);

        params.page = DEFAULT_PAGE_NUMBER;
        params.size = selectedSize;

        return { ...params };
      });
    },
    [getParams]
  );

  const onPageChange = useCallback(
    (selectedPage: number) => {
      setFilters((prev) => {
        const params = getParams(prev);

        params.page = selectedPage;

        return { ...params };
      });
    },
    [getParams]
  );

  const onStatusChange = useCallback(
    (selectedStatus: string | null) => {
      setFilters((prev) => {
        const params = getParams(prev);

        if (!selectedStatus) {
          delete params.status;
        } else {
          params.status = selectedStatus;
        }

        return { ...params };
      });
    },
    [getParams]
  );

  const onRoleChange = useCallback(
    (selectedRole: string | null) => {
      setFilters((prev) => {
        const params = getParams(prev);

        if (!selectedRole) {
          delete params.role;
        } else {
          params.role = selectedRole;
        }

        return { ...params };
      });
    },
    [getParams]
  );

  const onHeaderClick = useCallback(
    (name: string) => {
      const order = filters.sortBy === name && filters.order === ORDER_BY.ASC ? ORDER_BY.DESC : ORDER_BY.ASC;
      setFilters((prev) => ({
        ...prev,
        sortBy: name,
        order,
      }));

      setColumnToSortBy(name);
      setIsAscendingOrdered(order === ORDER_BY.ASC);
    },
    [filters.order, filters.sortBy]
  );

  useEffect(() => {
    updateUrl({
      ...filters,
    });
  }, [filters, updateUrl]);

  useEffect(() => {
    if (isObjectEmpty(queryParams)) {
      return;
    }

    const fetchAllNotes = async () => {
      setIsFetchingNotes(true);

      try {
        const page = getQueryNumberValue(queryParams.page) ?? DEFAULT_PAGE_NUMBER;
        const size = getQueryNumberValue(queryParams.size) ?? filters.size;
        const params = {
          page,
          size,
          status: getQueryStringValue(queryParams.status),
          role: getQueryStringValue(queryParams.role),
          sortBy: getQueryStringValue(queryParams.sortBy),
          order: getQueryStringValue(queryParams.order),
        };

        const { data, meta } = await noteService.fetchNotes<NoteSummaryItem>(params);

        const total = Number(meta.total ?? 0);
        const nextPageCount = Math.ceil(total / size);
        setPageData(meta);

        const mappedNotes = data.map((datum, index) => {
          return {
            ...datum,
            sn: (page - 1) * size + (index + 1),
          };
        });

        setNotes(mappedNotes);
        setPageCount(nextPageCount);
      } catch (error) {
        handleError(error);
      } finally {
        setIsFetchingNotes(false);
      }
    };

    void fetchAllNotes();
  }, [filters.size, queryParams, search]);

  return {
    filters,
    queryParams,
    pageData,
    pageCount,
    columnToSortBy,
    isAscendingOrdered,
    notes,
    isFetchingNotes,
    onPageChange,
    onPageSizeChange,
    onStatusChange,
    onRoleChange,
    onHeaderClick,
  };
}
